import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {ForkygramFollowRequestData} from 'types'
import {useTranslation} from 'i18n'
import {IMAGE_ASSET, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {requestData} from 'services'
import {Image, ListLazy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {TemplateFollowRequestItem} from 'pages/template'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  border-radius: ${convertUnit(16)} ${convertUnit(16)} 0 0;
  margin-top: ${convertUnit(20)};
  max-width: ${convertUnit(560)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    margin-top: 0;
    border-radius: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledEmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`
const StyledEmptyImg = styled(Image)`
  object-fit: contain;
  margin-bottom: ${convertUnit(25)};
  max-width: ${convertUnit(200)};
  max-height: ${convertUnit(165)};
`
const StyledEmptyMessage = styled(Paragraph)`
  text-align: center;
`

export default function ForkygramFollowRequest() {
  const {translate} = useTranslation()
  const emptyImg = IMAGE_ASSET('giftshop', 'not-found.png')
  const stateData = useState<ReadonlyArray<ForkygramFollowRequestData>>([])
  const setData = stateData[1]

  const handleGetFollowRequest = useCallback(
    async (page: number, limit: number) => {
      const result = await requestData('giftshop_get_follower_request_list', {
        params: {page, limit},
      })
      if (typeof result !== 'string' && result.status === 200) {
        return result.data.result
      }
      return null
    },
    [],
  )

  const handleRemoveRequest = useCallback(
    (item: ForkygramFollowRequestData) => {
      setData((prev) => prev.filter((x) => x.id !== item.id))
    },
    [setData],
  )

  const handleRenderItem = useCallback(
    (item: ForkygramFollowRequestData) => (
      <TemplateFollowRequestItem
        data={item}
        onRemoveRequest={handleRemoveRequest}
      />
    ),
    [handleRemoveRequest],
  )

  const handleRenderEmpty = useMemo(
    () => (
      <StyledEmptyContainer>
        <div>
          <StyledEmptyImg src={emptyImg} alt={emptyImg} />
        </div>
        <StyledEmptyMessage fontSize="l" fontWeight="bold">
          {translate('forkygram:followRequestEmptyTitle')}
        </StyledEmptyMessage>
        <StyledEmptyMessage fontSize="m" color="gray_3">
          {translate('forkygram:followRequestEmptyDescription')}
        </StyledEmptyMessage>
      </StyledEmptyContainer>
    ),
    [emptyImg, translate],
  )
  return (
    <StyledContainer>
      <StyledContentContainer>
        <ListLazy
          keyExtractor={(item) => item.id}
          renderItem={handleRenderItem}
          listEmptyElement={handleRenderEmpty}
          scrollbar={false}
          loadData={handleGetFollowRequest}
          stateData={stateData}
        />
      </StyledContentContainer>
    </StyledContainer>
  )
}
